import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandWindows,
  IconKey,
  IconLink,
  IconMail,
  IconShieldLock,
  IconUnlink,
  IconUserPlus,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserIdentitySummary } from '@/api';
import type { ConnectedAccountsApi } from './types';
import type { FlowStep } from './useConnectedAccountsFlow';
import { useConnectedAccountsFlow } from './useConnectedAccountsFlow';

function methodIcon(method: UserIdentitySummary['method']): ReactNode {
  switch (method) {
    case 'Google':
      return <IconBrandGoogle size={18} />;
    case 'GitHub':
      return <IconBrandGithub size={18} />;
    case 'Microsoft':
      return <IconBrandWindows size={18} />;
    case 'EmailMagicLink':
      return <IconMail size={18} />;
    case 'Nostr':
      return <IconKey size={18} />;
    case 'Oidc':
      return <IconShieldLock size={18} />;
    default:
      return <IconShieldLock size={18} />;
  }
}

function methodLabel(method: UserIdentitySummary['method'], t: TFunction): string {
  switch (method) {
    case 'EmailMagicLink':
      return t('connectedAccounts.methodEmailMagicLink', 'Email magic link');
    case 'Oidc':
      // Reuse the login page's brand label so the two stay identical.
      return t('login.oidcSsoTab', 'GoFranz SSO');
    default:
      return method;
  }
}

function formatDate(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export interface ConnectedAccountsProps {
  api: ConnectedAccountsApi;
  /** Notifications instance (`@mantine/notifications`) for success/error toasts. */
  notifications?: { show: (n: { title?: string; message: string; color?: string }) => void };
}

/**
 * Lists the account's linked sign-in identities and drives the link/unlink
 * flows. Unlink is hidden for the primary identity, the magic-link pseudo-entry
 * (`id: null`), and whenever only one usable (real-id) method remains.
 */
export function ConnectedAccounts({ api, notifications }: ConnectedAccountsProps) {
  const { t } = useTranslation();
  const flow = useConnectedAccountsFlow(api);

  const [identities, setIdentities] = useState<UserIdentitySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | 'link' | null>(null);
  const [emailPending, setEmailPending] = useState(false);
  const [nostrSignStep, setNostrSignStep] = useState<Extract<
    FlowStep,
    { kind: 'nostr_sign_required' }
  > | null>(null);
  const [signing, setSigning] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await api.listIdentities();
      setIdentities(data);
    } catch (e: any) {
      setError(e?.message || t('connectedAccounts.errorLoad', 'Could not load connected accounts'));
    }
  }, [api, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Identities with a real (deletable) id; magic-link pseudo-entries (id:null)
  // don't count toward the "must keep at least one usable method" guard.
  const usableCount = (identities ?? []).filter((i) => i.id !== null).length;

  // Linking policy: SSO may be linked only onto a local-method account that has
  // no OIDC identity yet. Once SSO is present (origin or linked), no further
  // linking is offered.
  const hasOidc = (identities ?? []).some((i) => i.method === 'Oidc');

  const handleStep = (step: FlowStep) => {
    switch (step.kind) {
      case 'email_pending':
        setEmailPending(true);
        break;
      case 'nostr_sign_required':
        // NIP-07 signing needs a user gesture; surface a button to invoke it.
        setNostrSignStep(step);
        break;
      case 'completed':
        notifications?.show({
          message: t('connectedAccounts.linkedSuccess', 'Account linked'),
          color: 'green',
        });
        refresh();
        break;
      case 'error':
        notifications?.show({
          message: t(`connectedAccounts.error.${step.code}`, t('connectedAccounts.errorGeneric')),
          color: 'red',
        });
        break;
      // 'redirecting' — browser is navigating away; nothing to do.
    }
  };

  const onLink = async () => {
    // guard against rapid double-submit overwriting the in-flight step-up
    if (busyId) return;
    setBusyId('link');
    try {
      handleStep(await flow.startLink());
    } catch (e: any) {
      notifications?.show({
        message: e?.message || t('connectedAccounts.errorGeneric'),
        color: 'red',
      });
    } finally {
      setBusyId(null);
    }
  };

  const onUnlink = async (id: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      handleStep(await flow.startUnlink(id));
    } catch (e: any) {
      notifications?.show({
        message: e?.message || t('connectedAccounts.errorGeneric'),
        color: 'red',
      });
    } finally {
      setBusyId(null);
    }
  };

  const onConfirmNostrSign = async () => {
    if (!nostrSignStep) return;
    setSigning(true);
    const pending = nostrSignStep;
    setNostrSignStep(null);
    try {
      handleStep(await pending.sign());
    } finally {
      setSigning(false);
    }
  };

  if (identities === null && !error) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Modal
        opened={nostrSignStep !== null}
        onClose={() => setNostrSignStep(null)}
        title={t('connectedAccounts.nostrSignTitle', 'Confirm with Nostr')}
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t(
              'connectedAccounts.nostrSignBody',
              "Sign a challenge with your Nostr extension to confirm it's you."
            )}
          </Text>
          <Button onClick={onConfirmNostrSign} loading={signing}>
            {t('connectedAccounts.nostrSignButton', 'Sign with Nostr')}
          </Button>
        </Stack>
      </Modal>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {error}
        </Alert>
      )}

      {emailPending && (
        <Alert
          icon={<IconMail size={16} />}
          color="blue"
          title={t('connectedAccounts.checkEmailTitle', 'Check your email')}
        >
          {t(
            'connectedAccounts.checkEmail',
            'We sent a confirmation link to your email. Open it to finish.'
          )}
        </Alert>
      )}

      <Stack gap="sm">
        {(identities ?? []).map((identity) => {
          const canUnlink = identity.id !== null && !identity.is_primary && usableCount > 1;
          const rowKey = identity.id ?? `pseudo-${identity.method}-${identity.display_key}`;
          return (
            <Card key={rowKey} withBorder radius="md" padding="md">
              <Group justify="space-between" wrap="nowrap">
                <Group wrap="nowrap" gap="sm" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" radius="md" size="lg">
                    {methodIcon(identity.method)}
                  </ThemeIcon>
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600} size="sm">
                        {methodLabel(identity.method, t)}
                      </Text>
                      {identity.is_primary && (
                        <Badge size="sm" variant="light" color="blue" radius="sm">
                          {t('connectedAccounts.primary', 'Primary')}
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed" truncate>
                      {identity.display_key}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {identity.linked_via
                        ? t('connectedAccounts.linkedVia', 'Linked via {{via}}', {
                            via: identity.linked_via,
                          })
                        : t('connectedAccounts.added', 'Added')}{' '}
                      {formatDate(identity.created_at)}
                      {identity.last_login_at
                        ? ` · ${t('connectedAccounts.lastLogin', 'last login')} ${formatDate(identity.last_login_at)}`
                        : ''}
                    </Text>
                  </Stack>
                </Group>
                {canUnlink && (
                  <Tooltip label={t('connectedAccounts.unlink', 'Unlink')} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => onUnlink(identity.id as string)}
                      loading={busyId === identity.id}
                      disabled={busyId !== null && busyId !== identity.id}
                      aria-label={t('connectedAccounts.unlink', 'Unlink')}
                    >
                      <IconUnlink size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Card>
          );
        })}
      </Stack>

      {hasOidc ? (
        <Alert icon={<IconShieldLock size={16} />} color="gray" variant="light">
          {t(
            'connectedAccounts.ssoConnected',
            'Single sign-on is connected. Accounts using SSO are managed by your identity provider and cannot link additional methods.'
          )}
        </Alert>
      ) : (
        <>
          <Group>
            <Button
              leftSection={<IconUserPlus size={16} />}
              onClick={onLink}
              loading={busyId === 'link'}
              disabled={busyId !== null && busyId !== 'link'}
            >
              {t('connectedAccounts.linkSso', 'Connect single sign-on (SSO)')}
            </Button>
          </Group>

          <Group gap={6} c="dimmed">
            <IconLink size={14} />
            <Text size="xs">
              {t(
                'connectedAccounts.hint',
                'Connect SSO to also sign in to this account with your single sign-on provider.'
              )}
            </Text>
          </Group>
        </>
      )}
    </Stack>
  );
}
